import React, { useState, useEffect } from 'react';
import { apiRequest } from '../api';

const CreateIntegrationModal = ({ onClose, onSave, formData, setFormData, errors, setErrors, isEdit = false, isSubmitting = false }) => {
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
    const [coreFieldMappingsText, setCoreFieldMappingsText] = useState('');
    const [fieldMappingsText, setFieldMappingsText] = useState('');
    const apiUrl = process.env.REACT_APP_API_BASE_URL;

    // Fetch templates when modal opens
    useEffect(() => {
        fetchTemplates();
    }, []);

    // Handle edit mode - set selected template from formData
    useEffect(() => {
        if (isEdit && formData._selectedTemplate) {
            setSelectedTemplate(formData._selectedTemplate);
            
            // Initialize text areas with existing mappings when editing
            const fieldMappings = formData.configuration?.field_mappings || {};
            const coreMappings = fieldMappings.core_field_mappings || {};
            const fieldMappingsObj = fieldMappings.field_mappings || {};
            
            setCoreFieldMappingsText(Object.entries(coreMappings)
                .map(([key, value]) => `${key}:${value}`)
                .join('\n'));
            
            setFieldMappingsText(Object.entries(fieldMappingsObj)
                .map(([key, value]) => `${key}:${value}`)
                .join('\n'));
        }
    }, [isEdit, formData._selectedTemplate, formData.configuration]);

    const fetchTemplates = async () => {
        setIsLoadingTemplates(true);
        try {
            const token = localStorage.getItem('token');
            const response = await apiRequest(`${apiUrl}/integration-templates/`, 'GET', null, token);
            
            if (response && response.templates) {
                setTemplates(response.templates);
            } else {
                setTemplates([]);
            }
        } catch (error) {
            setTemplates([]);
        } finally {
            setIsLoadingTemplates(false);
        }
    };

    const handleTemplateSelect = (templateId) => {
        const template = templates.find(t => t.id === templateId);
        setSelectedTemplate(template);
        
        if (template) {
            // Load complete template configuration
            const templateConfig = template.configuration || {};
            const fieldMappings = templateConfig.field_mappings || {};
            
            setFormData(prev => ({
                ...prev,
                template_id: templateId,
                configuration: {
                    endpoints: {
                        base_url: templateConfig.endpoints?.base_url || '',
                        submit_data: {
                            url: '', // Clear hardcoded URLs
                            method: templateConfig.endpoints?.submit_data?.method || 'POST',
                            content_type: templateConfig.endpoints?.submit_data?.content_type || 'application/json'
                        },
                        test_connection: {
                            url: '', // Clear hardcoded URLs
                            method: templateConfig.endpoints?.test_connection?.method || 'GET'
                        }
                    },
                    authentication: templateConfig.authentication || {},
                    field_mappings: {
                        type: fieldMappings.type || 'direct_match',
                        core_fields: fieldMappings.core_fields || '',
                        mappings: fieldMappings.mappings || '',
                        core_field_mappings: fieldMappings.core_field_mappings || {},
                        field_mappings: fieldMappings.field_mappings || {},
                        extra_field_name: fieldMappings.extra_field_name || 'extra_args'
                    },
                    sync_settings: templateConfig.sync_settings || {}
                },
                // Load sync settings from template as defaults
                sync_settings: {
                    mode: templateConfig.sync_settings?.mode || 'single',
                    batch_size: templateConfig.sync_settings?.batch_size || 50,
                    interval_minutes: templateConfig.sync_settings?.interval_minutes || 15,
                    records_per_interval: templateConfig.sync_settings?.records_per_interval || null,
                    rate_limit: templateConfig.sync_settings?.rate_limit || { requests_per_minute: 60 }
                }
            }));
            
            // Initialize text areas with template mappings
            const coreMappings = fieldMappings.core_field_mappings || {};
            const fieldMappingsObj = fieldMappings.field_mappings || {};
            
            setCoreFieldMappingsText(Object.entries(coreMappings)
                .map(([key, value]) => `${key}:${value}`)
                .join('\n'));
            
            setFieldMappingsText(Object.entries(fieldMappingsObj)
                .map(([key, value]) => `${key}:${value}`)
                .join('\n'));
        } else {
            setFormData(prev => ({
                ...prev,
                template_id: templateId,
                configuration: {
                    endpoints: {},
                    authentication: {},
                    field_mappings: {
                        type: 'direct_match',
                        core_fields: '',
                        mappings: ''
                    },
                    sync_settings: {}
                }
            }));
        }
        setErrors(prev => ({ ...prev, template_id: '' }));
    };

    const updateConfiguration = (section, field, value) => {
        setFormData(prev => ({
            ...prev,
            configuration: {
                ...prev.configuration,
                [section]: {
                    ...prev.configuration[section],
                    [field]: value
                }
            }
        }));
    };

    const updateSyncSettings = (field, value) => {
        setFormData(prev => ({
            ...prev,
            sync_settings: {
                ...prev.sync_settings,
                [field]: value
            }
        }));
    };

    const updateNestedSyncSettings = (section, field, value) => {
        setFormData(prev => ({
            ...prev,
            sync_settings: {
                ...prev.sync_settings,
                [section]: {
                    ...prev.sync_settings[section],
                    [field]: value
                }
            }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        console.log('CreateIntegrationModal - formData before submit:', formData);
        console.log('CreateIntegrationModal - sync_enabled:', formData.sync_enabled);
        console.log('CreateIntegrationModal - is_enabled:', formData.is_enabled);
        
        // Validate form
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (!formData.template_id) newErrors.template_id = 'Template is required';
        
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        // Get university_id and add it to formData
        const universityId = localStorage.getItem('universityId');
        
        if (!universityId) {
            setErrors({ submit: 'University ID not found. Please log in again.' });
            return;
        }
        
        // Add university_id to the form data and clean up temporary fields
        const cleanFormData = {
            ...formData,
            university_id: universityId
        };
        
        // Remove temporary template data
        delete cleanFormData._selectedTemplate;
        
        setFormData(cleanFormData);
        
        console.log('CreateIntegrationModal - calling onSave with formData:', cleanFormData);
        
        // Call the parent's onSave function instead of making API call here
        onSave();
    };

    const renderSyncSettingsConfig = () => {
        return (
            <div style={{ marginTop: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '5px' }}>
                <h3 style={{ marginBottom: '15px', fontSize: '16px' }}>
                    Sync Settings
                    <span style={{ marginLeft: '8px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                </h3>
                
                <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                            type="checkbox"
                            checked={formData.sync_enabled}
                            onChange={(e) => setFormData(prev => ({ ...prev, sync_enabled: e.target.checked }))}
                        />
                        <span style={{ fontWeight: 'bold' }}>Enable Scheduled Sync</span>
                    </label>
                </div>

                {formData.sync_enabled && (
                    <>
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Sync Mode
                            </label>
                            <select
                                value={formData.sync_settings.mode || 'single'}
                                onChange={(e) => updateSyncSettings('mode', e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px'
                                }}
                            >
                                <option value="single">Single Record</option>
                                <option value="batch">Batch Processing</option>
                            </select>
                        </div>

                        {formData.sync_settings.mode === 'batch' && (
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                    Batch Size
                                </label>
                                <input
                                    type="number"
                                    value={formData.sync_settings.batch_size || 50}
                                    onChange={(e) => updateSyncSettings('batch_size', parseInt(e.target.value) || 50)}
                                    min="1"
                                    max="1000"
                                    placeholder="50"
                                    style={{
                                        width: '100%',
                                        padding: '8px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px'
                                    }}
                                />
                            </div>
                        )}

                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Interval (minutes)
                            </label>
                            <input
                                type="number"
                                value={formData.sync_settings.interval_minutes || 15}
                                onChange={(e) => updateSyncSettings('interval_minutes', parseInt(e.target.value) || 15)}
                                min="1"
                                max="1440"
                                placeholder="15"
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px'
                                }}
                            />
                        </div>

                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Records Per Interval
                            </label>
                            <input
                                type="number"
                                value={formData.sync_settings.records_per_interval || ''}
                                onChange={(e) => updateSyncSettings('records_per_interval', e.target.value ? parseInt(e.target.value) : null)}
                                min="1"
                                max="10000"
                                placeholder="Leave empty for no limit"
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px'
                                }}
                            />
                        </div>

                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Rate Limit (requests per minute)
                            </label>
                            <input
                                type="number"
                                value={formData.sync_settings.rate_limit?.requests_per_minute || 60}
                                onChange={(e) => updateNestedSyncSettings('rate_limit', 'requests_per_minute', parseInt(e.target.value) || 60)}
                                min="1"
                                max="1000"
                                placeholder="60"
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px'
                                }}
                            />
                        </div>
                    </>
                )}
            </div>
        );
    };

    const renderAuthenticationFields = () => {
        if (!selectedTemplate || !selectedTemplate.configuration?.authentication) return null;
        const authConfig = selectedTemplate.configuration.authentication;
        const authType = authConfig.type;
        const fields = authConfig.fields || [];

        if (authType === 'none') {
            return (
                <div style={{ marginBottom: '10px', color: '#666', fontStyle: 'italic' }}>
                    No authentication required for this integration.
                </div>
            );
        }

        return (
            <div style={{ marginBottom: '15px' }}>
                <h4 style={{ marginBottom: '10px', fontSize: '14px' }}>Authentication</h4>
                {fields.map((field) => (
                    <div key={field} style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                            {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </label>
                        <input
                            type={field.toLowerCase().includes('password') ? 'password' : 'text'}
                            value={formData.configuration.authentication?.[field] || ''}
                            onChange={(e) => updateConfiguration('authentication', field, e.target.value)}
                            placeholder={field.replace(/_/g, ' ')}
                            style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #ddd',
                                borderRadius: '4px'
                            }}
                        />
                    </div>
                ))}
                {authType === 'api_key' && (
                    <div style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                            Header Name
                        </label>
                        <input
                            type="text"
                            value={formData.configuration.authentication?.header_name || 'X-API-Key'}
                            onChange={(e) => updateConfiguration('authentication', 'header_name', e.target.value)}
                            placeholder="X-API-Key"
                            style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #ddd',
                                borderRadius: '4px'
                            }}
                        />
                    </div>
                )}
                {authType === 'api_key_in_form' && (
                    <div style={{ 
                        marginBottom: '10px', 
                        padding: '8px', 
                        backgroundColor: '#e3f2fd', 
                        border: '1px solid #2196f3', 
                        borderRadius: '4px',
                        fontSize: '12px'
                    }}>
                        <strong>ℹ️ API Key in Form Data:</strong> The API key will be included in the request body as a form field, not in the headers.
                    </div>
                )}
                {authType === 'oauth2' && (
                    <>
                        <div style={{ marginBottom: '10px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Token URL
                            </label>
                            <input
                                type="url"
                                value={formData.configuration.authentication?.token_url || ''}
                                onChange={(e) => updateConfiguration('authentication', 'token_url', e.target.value)}
                                placeholder="https://oauth.example.com/token"
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px'
                                }}
                            />
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Scope
                            </label>
                            <input
                                type="text"
                                value={formData.configuration.authentication?.scope || ''}
                                onChange={(e) => updateConfiguration('authentication', 'scope', e.target.value)}
                                placeholder="read write"
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px'
                                }}
                            />
                        </div>
                    </>
                )}
            </div>
        );
    };

    const renderFieldMappingConfig = () => {
        if (!selectedTemplate || !selectedTemplate.configuration?.field_mappings) return null;
        const fieldMappings = selectedTemplate.configuration.field_mappings;
        const mappingType = formData.configuration.field_mappings?.type || fieldMappings.type;

        return (
            <div style={{ marginBottom: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '5px' }}>
                <h4 style={{ marginBottom: '15px', fontSize: '16px' }}>
                    Field Mapping Configuration
                    <span style={{ marginLeft: '8px', cursor: 'help', color: '#666', fontSize: '14px' }}>ℹ</span>
                </h4>
                
                <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Mapping Type
                        <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '12px' }}>ℹ</span>
                    </label>
                    <select
                        value={mappingType}
                        onChange={(e) => updateConfiguration('field_mappings', 'type', e.target.value)}
                        style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px'
                        }}
                    >
                        <option value="direct_match">Direct Match</option>
                        <option value="core_plus_extra">Core + Extra</option>
                        <option value="field_name_mapping">Field Name Mapping</option>
                        <option value="core_plus_extra_mapped">Core + Extra with Field Mapping</option>
                    </select>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                        Choose how form fields are mapped to CRM fields
                    </div>
                </div>

                {mappingType === 'direct_match' && (
                    <div>
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Required Fields
                                <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '12px' }}>ℹ</span>
                            </label>
                            <input
                                type="text"
                                value={formData.configuration.field_mappings?.required_fields?.join(', ') || fieldMappings.required_fields?.join(', ') || ''}
                                onChange={(e) => updateConfiguration('field_mappings', 'required_fields', e.target.value.split(',').map(f => f.trim()).filter(f => f))}
                                placeholder="First Name, Last Name, Email (comma-separated)"
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px'
                                }}
                            />
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                Form fields that must be present (comma-separated)
                            </div>
                        </div>
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Optional Fields
                                <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '12px' }}>ℹ</span>
                            </label>
                            <input
                                type="text"
                                value={formData.configuration.field_mappings?.optional_fields?.join(', ') || fieldMappings.optional_fields?.join(', ') || ''}
                                onChange={(e) => updateConfiguration('field_mappings', 'optional_fields', e.target.value.split(',').map(f => f.trim()).filter(f => f))}
                                placeholder="Phone, Address, Notes (comma-separated)"
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px'
                                }}
                            />
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                Form fields that may be present (comma-separated)
                            </div>
                        </div>
                    </div>
                )}

                {mappingType === 'core_plus_extra' && (
                    <div>
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Core Fields
                                <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '12px' }}>ℹ</span>
                            </label>
                            <input
                                type="text"
                                value={formData.configuration.field_mappings?.core_fields?.join(', ') || fieldMappings.core_fields?.join(', ') || ''}
                                onChange={(e) => updateConfiguration('field_mappings', 'core_fields', e.target.value.split(',').map(f => f.trim()).filter(f => f))}
                                placeholder="First Name, Last Name, Email (comma-separated)"
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px'
                                }}
                            />
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                Fields that will be sent directly to CRM (comma-separated)
                            </div>
                        </div>
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Extra Field Name
                                <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '12px' }}>ℹ</span>
                            </label>
                            <input
                                type="text"
                                value={formData.configuration.field_mappings?.extra_field_name || fieldMappings.extra_field_name || ''}
                                onChange={(e) => updateConfiguration('field_mappings', 'extra_field_name', e.target.value)}
                                placeholder="extra_args"
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px'
                                }}
                            />
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                Name of the field that will contain all non-core fields
                            </div>
                        </div>
                    </div>
                )}

                {mappingType === 'core_plus_extra_mapped' && (
                    <div>
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Core Field Mappings
                                <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '12px' }}>ℹ</span>
                            </label>
                            <textarea
                                value={coreFieldMappingsText}
                                onChange={(e) => {
                                    const rawText = e.target.value;
                                    setCoreFieldMappingsText(rawText);
                                    
                                    // Parse valid mappings and update form data
                                    const mappings = {};
                                    const lines = rawText.split('\n');
                                    lines.forEach(line => {
                                        const trimmedLine = line.trim();
                                        if (trimmedLine && trimmedLine.includes(':')) {
                                            const colonIndex = trimmedLine.indexOf(':');
                                            if (colonIndex > 0) {
                                                const key = trimmedLine.substring(0, colonIndex).trim();
                                                const value = trimmedLine.substring(colonIndex + 1).trim();
                                                if (key && value) {
                                                    mappings[key] = value;
                                                }
                                            }
                                        }
                                    });
                                    
                                    updateConfiguration('field_mappings', 'core_field_mappings', mappings);
                                }}
                                placeholder="First Name:first_name\nLast Name:last_name\nEmail:email_address"
                                rows={5}
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    fontFamily: 'monospace',
                                    fontSize: '12px'
                                }}
                            />
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                Map core form fields to specific CRM field names (form_field:crm_field). Add new lines to create additional mappings.
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    const templateMappings = selectedTemplate.configuration.field_mappings.core_field_mappings || {};
                                    updateConfiguration('field_mappings', 'core_field_mappings', templateMappings);
                                    
                                    // Update the text area with template mappings
                                    setCoreFieldMappingsText(Object.entries(templateMappings)
                                        .map(([key, value]) => `${key}:${value}`)
                                        .join('\n'));
                                }}
                                style={{
                                    marginTop: '5px',
                                    padding: '4px 8px',
                                    fontSize: '11px',
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #ddd',
                                    borderRadius: '3px',
                                    cursor: 'pointer'
                                }}
                            >
                                Reset to Template Defaults
                            </button>
                        </div>
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Extra Field Name
                                <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '12px' }}>ℹ</span>
                            </label>
                            <input
                                type="text"
                                value={formData.configuration.field_mappings?.extra_field_name || fieldMappings.extra_field_name || ''}
                                onChange={(e) => updateConfiguration('field_mappings', 'extra_field_name', e.target.value)}
                                placeholder="extra_args"
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px'
                                }}
                            />
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                Name of the field that will contain all non-core fields
                            </div>
                        </div>
                    </div>
                )}

                {mappingType === 'field_name_mapping' && (
                    <div>
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Field Mappings
                                <span style={{ marginLeft: '4px', cursor: 'help', color: '#666', fontSize: '12px' }}>ℹ</span>
                            </label>
                            <textarea
                                value={fieldMappingsText}
                                onChange={(e) => {
                                    const rawText = e.target.value;
                                    setFieldMappingsText(rawText);
                                    
                                    // Parse valid mappings and update form data
                                    const mappings = {};
                                    const lines = rawText.split('\n');
                                    lines.forEach(line => {
                                        const trimmedLine = line.trim();
                                        if (trimmedLine && trimmedLine.includes(':')) {
                                            const colonIndex = trimmedLine.indexOf(':');
                                            if (colonIndex > 0) {
                                                const key = trimmedLine.substring(0, colonIndex).trim();
                                                const value = trimmedLine.substring(colonIndex + 1).trim();
                                                if (key && value) {
                                                    mappings[key] = value;
                                                }
                                            }
                                        }
                                    });
                                    
                                    updateConfiguration('field_mappings', 'field_mappings', mappings);
                                }}
                                placeholder="First Name:first_name\nLast Name:last_name\nEmail:email_address"
                                rows={5}
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    fontFamily: 'monospace',
                                    fontSize: '12px'
                                }}
                            />
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                Map form field names to CRM field names (form_field:crm_field). Add new lines to create additional mappings.
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    const templateMappings = selectedTemplate.configuration.field_mappings.field_mappings || {};
                                    updateConfiguration('field_mappings', 'field_mappings', templateMappings);
                                    
                                    // Update the text area with template mappings
                                    setFieldMappingsText(Object.entries(templateMappings)
                                        .map(([key, value]) => `${key}:${value}`)
                                        .join('\n'));
                                }}
                                style={{
                                    marginTop: '5px',
                                    padding: '4px 8px',
                                    fontSize: '11px',
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #ddd',
                                    borderRadius: '3px',
                                    cursor: 'pointer'
                                }}
                            >
                                Reset to Template Defaults
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
        }}>
            <div style={{
                backgroundColor: '#fff',
                padding: '20px',
                borderRadius: '8px',
                width: '90%',
                maxWidth: '800px',
                maxHeight: '90vh',
                overflow: 'auto'
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px'
                }}>
                    <h2 style={{ fontSize: '24px', margin: 0 }}>{isEdit ? 'Edit Integration' : 'Create Integration'}</h2>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '24px',
                            cursor: 'pointer',
                            padding: '5px'
                        }}
                    >
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit} style={{ position: 'relative' }}>
                    {isSubmitting && (
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 10,
                            borderRadius: '8px'
                        }}>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '24px', marginBottom: '10px' }}>⏳</div>
                                <div>{isEdit ? 'Updating Integration...' : 'Creating Integration...'}</div>
                            </div>
                        </div>
                    )}
                    
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                            Integration Name *
                        </label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => {
                                setFormData(prev => ({ ...prev, name: e.target.value }));
                                setErrors(prev => ({ ...prev, name: '' }));
                            }}
                            style={{
                                width: '100%',
                                padding: '8px',
                                border: errors.name ? '1px solid red' : '1px solid #ddd',
                                borderRadius: '4px'
                            }}
                            placeholder="Enter integration name"
                        />
                        {errors.name && <span style={{ color: 'red', fontSize: '12px' }}>{errors.name}</span>}
                    </div>

                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                            Description
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                minHeight: '80px'
                            }}
                            placeholder="Enter description"
                        />
                    </div>

                    {!isEdit ? (
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Template *
                            </label>
                            <select
                                value={formData.template_id}
                                onChange={(e) => handleTemplateSelect(e.target.value)}
                                disabled={isLoadingTemplates}
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: errors.template_id ? '1px solid red' : '1px solid #ddd',
                                    borderRadius: '4px'
                                }}
                            >
                                <option value="">
                                    {isLoadingTemplates ? 'Loading templates...' : 'Select a template'}
                                </option>
                                {templates.map(template => (
                                    <option key={template.id} value={template.id}>
                                        {template.display_name}
                                    </option>
                                ))}
                            </select>
                            {errors.template_id && <span style={{ color: 'red', fontSize: '12px' }}>{errors.template_id}</span>}
                            {templates.length === 0 && !isLoadingTemplates && (
                                <span style={{ color: 'orange', fontSize: '12px' }}>No templates available</span>
                            )}
                        </div>
                    ) : (
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                Template
                            </label>
                            <input
                                type="text"
                                value={selectedTemplate?.display_name || 'Loading...'}
                                disabled
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    backgroundColor: '#f8f9fa'
                                }}
                            />
                        </div>
                    )}

                    {/* Template Configuration */}
                    {selectedTemplate && (
                        <div style={{ marginTop: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '5px' }}>
                            <h3 style={{ marginBottom: '15px', fontSize: '16px' }}>Template Configuration</h3>
                            
                            {/* Endpoints */}
                            {selectedTemplate.configuration?.endpoints && (
                                <div style={{ marginBottom: '15px' }}>
                                    <h4 style={{ marginBottom: '10px', fontSize: '14px' }}>Endpoints</h4>
                                    <div style={{ marginBottom: '10px' }}>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                            Base URL
                                        </label>
                                        <input
                                            type="url"
                                            value={formData.configuration.endpoints?.base_url || ''}
                                            onChange={(e) => updateConfiguration('endpoints', 'base_url', e.target.value)}
                                            placeholder="https://api.example.com"
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px'
                                            }}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Authentication */}
                            {renderAuthenticationFields()}

                            {/* Field Mapping Configuration */}
                            {renderFieldMappingConfig()}
                        </div>
                    )}

                    {/* Sync Settings */}
                    {renderSyncSettingsConfig()}

                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                                type="checkbox"
                                checked={formData.is_active}
                                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                            />
                            <span style={{ fontWeight: 'bold' }}>Active</span>
                        </label>
                    </div>

                    {errors.submit && (
                        <div style={{ color: 'red', marginBottom: '15px', fontSize: '14px' }}>
                            {errors.submit}
                        </div>
                    )}

                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '10px',
                        marginTop: '20px'
                    }}>
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isSubmitting}
                            style={{
                                padding: '10px 20px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                backgroundColor: '#f8f9fa',
                                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                opacity: isSubmitting ? 0.6 : 1
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            style={{
                                padding: '10px 20px',
                                border: 'none',
                                borderRadius: '4px',
                                backgroundColor: isSubmitting ? '#6c757d' : '#007bff',
                                color: 'white',
                                cursor: isSubmitting ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {isSubmitting ? (
                                <>
                                    <span style={{ marginRight: '8px' }}>⏳</span>
                                    {isEdit ? 'Updating...' : 'Creating...'}
                                </>
                            ) : (
                                isEdit ? 'Update Integration' : 'Create Integration'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateIntegrationModal; 